import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from 'sonner';

export default function EditUser() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [salesReps, setSalesReps] = useState([]);
  const [assignedLeads, setAssignedLeads] = useState([]);

  useEffect(() => {
    fetchUser();
    fetchSalesReps();
    fetchAssignedLeads();
  }, [userId]);

  async function fetchUser() {
    try {
      const data = await api.getUser(userId);
      setUser(data);
    } catch (error) {
      console.error('Fehler beim Laden des Benutzers:', error);
      toast.error('Benutzer konnte nicht geladen werden');
      navigate('/admin/users');
    }
  }

  async function updateUser(updates) {
    setLoading(true);
    try {
      await api.updateUser(userId, updates);
      toast.success('Benutzer aktualisiert');
      fetchUser();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Benutzers:', error);
      toast.error('Benutzer konnte nicht aktualisiert werden');
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordChange() {
    if (!newPassword) {
      toast.error('Bitte geben Sie ein neues Passwort ein');
      return;
    }

    setLoading(true);
    try {
      await api.updateUser(userId, { password: newPassword });
      toast.success('Passwort wurde aktualisiert');
      setNewPassword('');
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Passworts:', error);
      toast.error('Passwort konnte nicht aktualisiert werden');
    } finally {
      setLoading(false);
    }
  }

  async function fetchSalesReps() {
    try {
      const data = await api.getUsers();
      setSalesReps(data.filter(u => u.role === 'sales' && u.status === 'active' && u.id !== userId));
    } catch (error) {
      console.error('Fehler beim Laden der Vertriebler:', error);
      toast.error('Vertriebler konnten nicht geladen werden');
    }
  }

  async function fetchAssignedLeads() {
    try {
      const data = await api.getLeads();
      setAssignedLeads(data.filter(lead => lead.sales_rep_id === userId));
    } catch (error) {
      console.error('Fehler beim Laden der zugewiesenen Leads:', error);
      toast.error('Zugewiesene Leads konnten nicht geladen werden');
    }
  }

  async function transferLeads(newSalesRepId) {
    if (!newSalesRepId) return;
    
    setLoading(true);
    try {
      await api.transferLeads(userId, newSalesRepId);
      toast.success('Leads wurden übertragen');
      fetchAssignedLeads();
    } catch (error) {
      console.error('Fehler beim Übertragen der Leads:', error);
      toast.error('Leads konnten nicht übertragen werden');
    } finally {
      setLoading(false);
    }
  }

  if (!user) return <div>Lädt...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Benutzer bearbeiten</h1>
        <Button onClick={() => navigate('/admin/users')}>
          Zurück zur Übersicht
        </Button>
      </div>

      <div className="max-w-2xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Benutzerdaten</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={user.name}
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                onBlur={() => updateUser({ name: user.name })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                value={user.email}
                onChange={(e) => setUser({ ...user, email: e.target.value })}
                onBlur={() => updateUser({ email: user.email })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Rolle</Label>
              <Select
                value={user.role}
                onValueChange={(value) => updateUser({ role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="sales">Vertriebler</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={user.status}
                onValueChange={(value) => updateUser({ status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="invited">Eingeladen</SelectItem>
                  <SelectItem value="inactive">Inaktiv</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Passwort ändern</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Neues Passwort</Label>
              <div className="flex gap-2">
                <Input
                  id="password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Neues Passwort eingeben"
                />
                <Button 
                  onClick={handlePasswordChange}
                  disabled={loading || !newPassword}
                >
                  Passwort ändern
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {user?.role === 'sales' && (
          <Card>
            <CardHeader>
              <CardTitle>Lead-Verwaltung</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-2">
                  Diesem Vertriebler sind {assignedLeads.length} Leads zugewiesen.
                </p>
                <div className="flex gap-2">
                  <Select
                    onValueChange={transferLeads}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Leads übertragen an" />
                    </SelectTrigger>
                    <SelectContent>
                      {salesReps.map(rep => (
                        <SelectItem key={rep.id} value={rep.id}>
                          {rep.name || rep.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
} 